import React from "react";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import { createJob, listJobs } from "../../api/jobs";
import { createInspectionEntry, deleteInspectionEntry, listInspectionEntries } from "../../api/inspections";

export default function InspectionCalendar() {
  const [jobs, setJobs] = React.useState([]);
  const [jobId, setJobId] = React.useState("");
  const [manualJobName, setManualJobName] = React.useState("");
  const [scheduledFor, setScheduledFor] = React.useState("");
  const [note, setNote] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [loadingJobs, setLoadingJobs] = React.useState(true);
  const [loadingItems, setLoadingItems] = React.useState(false);
  const [busyCreate, setBusyCreate] = React.useState(false);
  const [busyDeleteId, setBusyDeleteId] = React.useState("");
  const [error, setError] = React.useState("");

  const refreshJobs = React.useCallback(async () => {
    setLoadingJobs(true);
    try {
      const rows = await listJobs({ limit: 300, offset: 0 });
      setJobs(Array.isArray(rows) ? rows : []);
      if (!jobId && rows?.length) setJobId(String(rows[0].id || ""));
    } catch (_e) {
      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  }, [jobId]);

  const refreshItems = React.useCallback(async () => {
    if (!jobId) {
      setItems([]);
      return;
    }
    setLoadingItems(true);
    try {
      const res = await listInspectionEntries({ jobId, limit: 500, offset: 0 });
      setItems(Array.isArray(res?.items) ? res.items : []);
    } catch (e) {
      setError(e?.message || "Failed to load scheduled inspections.");
      setItems([]);
    } finally {
      setLoadingItems(false);
    }
  }, [jobId]);

  React.useEffect(() => {
    refreshJobs();
  }, [refreshJobs]);

  React.useEffect(() => {
    refreshItems();
  }, [refreshItems]);

  const resolveJobId = async () => {
    if (String(jobId || "").trim()) return String(jobId).trim();
    const name = String(manualJobName || "").trim();
    if (!name) throw new Error("Select a job or enter a job name.");
    const match = (jobs || []).find((j) => String(j?.name || "").trim().toLowerCase() === name.toLowerCase());
    if (match?.id) return String(match.id);
    const created = await createJob(name);
    const id = String(created?.id || "").trim();
    if (!id) throw new Error("Failed to create job.");
    setJobId(id);
    setManualJobName("");
    await refreshJobs();
    return id;
  };

  const onAdd = async () => {
    setError("");
    setBusyCreate(true);
    try {
      const effectiveJobId = await resolveJobId();
      const when = String(scheduledFor || "").trim();
      if (!when) throw new Error("Select date and time.");
      await createInspectionEntry({
        job_id: effectiveJobId,
        scheduled_for: when,
        note: String(note || "").trim() || null,
      });
      setScheduledFor("");
      setNote("");
      await refreshItems();
    } catch (e) {
      setError(e?.message || "Failed to schedule inspection.");
    } finally {
      setBusyCreate(false);
    }
  };

  const onDelete = async (id) => {
    const entryId = String(id || "").trim();
    if (!entryId) return;
    setBusyDeleteId(entryId);
    setError("");
    try {
      await deleteInspectionEntry(entryId);
      await refreshItems();
    } catch (e) {
      setError(e?.message || "Failed to delete inspection.");
    } finally {
      setBusyDeleteId("");
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <Card>
        <CardHeader>
          <h1 className="text-xl font-semibold text-slate-900">Inspection Calendar</h1>
          <p className="mt-1 text-sm text-slate-500">Track scheduled inspections by job.</p>
        </CardHeader>
        <CardBody className="space-y-4">
          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{error}</p> : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-slate-700">
              Job
              <select
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800"
                disabled={loadingJobs}
                value={jobId}
                onChange={(e) => setJobId(e.target.value)}
              >
                <option value="">{loadingJobs ? "Loading jobs..." : "Select job"}</option>
                {jobs.map((job) => (
                  <option key={String(job?.id || "")} value={String(job?.id || "")}>
                    {String(job?.name || "(untitled)")} ({String(job?.id || "")})
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-slate-700">
              Or enter job name
              <Input
                className="mt-1"
                placeholder="New or existing job name"
                value={manualJobName}
                onChange={(e) => setManualJobName(e.target.value)}
              />
            </label>
            <label className="text-sm text-slate-700">
              Date & time
              <Input
                className="mt-1"
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
              />
            </label>
            <label className="text-sm text-slate-700">
              Note (optional)
              <Input
                className="mt-1"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Moisture check / adjuster walk-through"
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={onAdd} disabled={busyCreate}>{busyCreate ? "Saving..." : "Add Scheduled Inspection"}</Button>
            <Button variant="secondary" onClick={refreshItems} disabled={loadingItems || !jobId}>
              {loadingItems ? "Refreshing..." : "Refresh"}
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-slate-900">Scheduled Inspections</h2>
        </CardHeader>
        <CardBody>
          {items.length > 0 ? (
            <ul className="space-y-2 text-sm">
              {items.map((it) => (
                <li key={String(it?.id || "")} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                  <div>
                    <p className="font-medium text-slate-900">{String(it?.scheduled_for || "")}</p>
                    <p className="text-xs text-slate-600">{String(it?.note || "").trim() || "No note"}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(it?.id)}
                    disabled={busyDeleteId === String(it?.id || "")}
                  >
                    {busyDeleteId === String(it?.id || "") ? "Removing..." : "Remove"}
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">No scheduled inspections yet.</p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

